import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { InlineError, Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../api.ts";
import { apiError, ok } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { useCsrfHeaders } from "./operator.tsx";
import { consoleRefusal } from "./parts.tsx";
import { consoleKeys } from "./queries.ts";

/** The operator a session row names: name, else e-mail; never the sub (#351). */
function sessionOperator(operator: { email: string | null; name: string | null } | null) {
  return operator?.name ?? operator?.email ?? null;
}

/** Operator web sessions, revocable on demand (spec "Operator console"), on the Operators page. */
export function OperatorSessions() {
  const { t } = useTranslation();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const sessions = useQuery({
    queryKey: consoleKeys.sessions,
    queryFn: async () => ok(await api.api.operator.sessions.$get()),
    retry: false,
  });
  const revoke = useMutation({
    mutationFn: async (handle: string) => {
      const res = await api.api.operator.sessions[":handle"].$delete(
        { param: { handle } },
        headers,
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: consoleKeys.sessions }),
  });
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-fg-secondary">{t("console.sessions.lead")}</p>
      {sessions.isError ? (
        <Notice tone="danger">{sessions.error.message}</Notice>
      ) : !sessions.data ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
          {sessions.data.map((s) => (
            <li key={s.handle} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium text-fg">
                  {s.device ?? t("console.sessions.unknownDevice")}
                  {s.current ? (
                    <span className="ml-2 text-xs font-normal text-fg-muted">
                      {t("console.sessions.current")}
                    </span>
                  ) : null}
                </span>
                <span className="truncate text-xs text-fg-muted">
                  {t("console.sessions.meta", {
                    operator: sessionOperator(s.operator) ?? t("console.sessions.unknownOperator"),
                    lastSeen: formatDateTime(s.lastSeenAt),
                  })}
                </span>
              </span>
              {s.current ? null : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(s.handle)}
                >
                  {t("console.sessions.revoke")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {revoke.error ? <InlineError>{consoleRefusal(revoke.error)}</InlineError> : null}
    </div>
  );
}
